<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-red" sprite="clock" />
          Scheduled Reports
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-red" sprite="clock" />
            Scheduled Reports
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading || deleteProcessing"
          :columns="tableColumns"
        />
      </div>
    </div>

    <edit-form
      :editVisible="editVisible"
      :editProcessing="editProcessing"
      :clearFormFields="clearFormFields"
      :selectedReport="selectedReport"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import feathersClient from "../../../feathers/feathers-client";
import EditForm from "./components/editForm";
import { globalEventBus } from "../../../globalEventBus";
import { ExportToCsv } from "export-to-csv";
import { debounce } from "lodash";

export default {
  name: "ScheduledReports",

  data() {
    return {
      editProcessing: false,
      deleteProcessing: false,
      tableLoading: false,
      tableData: [],
      allData: [],
      search: "",
      visibleActionsId: "",
      editVisible: false,
      clearFormFields: false,
      reportChannel: null,

      selectedReport: null,
      total: 0,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-red" sprite="clock" />
                </div>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
          minWidth: 150,
        },
        {
          field: "module",
          title: "Module",
          sortable: true,
          minWidth: 100,
        },
        {
          field: "frequency",
          title: "Frequency",
          sortable: true,
          minWidth: 100,
        },
        {
          field: "creatorEmail",
          title: "Creator",
          sortable: true,
          minWidth: 150,
        },
        {
          field: "recipients",
          title: "Recipients",
          sortable: true,
          minWidth: 250,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [
                <div>
                  {Array.isArray(row.recipients) && row.recipients.join(", ")}
                </div>,
              ];
            },
          },
        },
        {
          field: "actions",
          title: "",
          minWidth: "100",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },

  components: {
    EditForm,
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
  },

  watch: {
    selectedTenant() {
      this.findScheduledReports();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.reportChannel } });
      this.joinSocketChannel();
    },
    user() {
      this.findScheduledReports();
      if (!this.reportChannel) {
        this.joinSocketChannel();
      }
    },
    search: debounce(function () {
      this.searchReports();
    }, 500),
  },

  methods: {
    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          name: item.name,
          type: item.type,
          module: item.module,
          frequency: item.frequency,
          creatorEmail: item.creatorEmail,
          recipients: JSON.stringify(item.recipients),
          parameters: JSON.stringify(item.parameters),
        }));

        const options = {
          filename: "scheduled_reports",
          showLabels: true,
          headers: [
            "Name",
            "Type",
            "Module",
            "Frequency",
            "Creator Email",
            "Recipients",
            "Parameters",
          ],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    searchReports() {
      this.tableData =
        this.search && this.search.length
          ? this.tableData.filter(
              (data) =>
                data.name.toLowerCase().includes(this.search.toLowerCase()) ||
                data.frequency
                  .toLowerCase()
                  .includes(this.search.toLowerCase()) ||
                data.module.toLowerCase().includes(this.search.toLowerCase()) ||
                data.creatorEmail
                  .toLowerCase()
                  .includes(this.search.toLowerCase())
            )
          : [...this.allData];

      this.total = this.tableData.length;
    },

    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins",
        tenantId: this.selectedTenant.id,
      };
      channelService.create(data).then(() => {
        this.reportChannel = `${data.roomType}/${data.tenantId}`;
      });
    },

    removeListeners() {
      feathersClient.service("scheduled-reports").removeListener("patched");
      feathersClient.service("scheduled-reports").removeListener("removed");
      globalEventBus.$off("appReconnected");
    },

    // ⭐️ General Form and Table Operations ⭐️ //
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    openEditModal(id) {
      this.clearFormFields = false;
      feathersClient
        .service("scheduled-reports")
        .get(id)
        .then((res) => {
          this.selectedReport = res;
          this.editVisible = true;
        });
    },

    editOperation(editBody) {
      this.$confirm("Process scheduled report edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.editProcessing = true;

          feathersClient
            .service("scheduled-reports")
            .patch(this.selectedReport.id, editBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited Successfully!",
                type: "success",
              });
              this.editProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation();
            })
            .catch((error) => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }

              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation() {
      this.clearFormFields = true;
      this.selectedReport = null;
      this.editVisible = false;
    },

    deleteOperation(id) {
      this.$confirm("Process scheduled report delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.deleteProcessing = true;

          feathersClient
            .service("scheduled-reports")
            .remove(id)
            .then(() => {
              this.deleteProcessing = false;
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success",
              });
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }

              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    findScheduledReports() {
      this.tableLoading = true;
      feathersClient
        .service("scheduled-reports")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
          },
        })
        .then((res) => {
          this.tableData = [...res];
          this.allData = [...res];
          this.total = res.length;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },
  },
  created() {
    // ⭐️ monitor socket connection ⭐️ //
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    // ⭐️ Record was edited ⭐️ //
    feathersClient.service("scheduled-reports").on("patched", (data) => {
      const updateIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const updateAllIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allData.splice(updateAllIndex, 1, data);
      }
    });

    // ⭐️ Record was deleted ⭐️ //
    feathersClient.service("scheduled-reports").on("removed", (data) => {
      const indexToRemove = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const indexAllToRemove = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allData.splice(indexAllToRemove, 1);
      }
    });
  },

  mounted() {
    if (this.user) {
      this.joinSocketChannel();
      this.findScheduledReports();
    }
  },

  beforeDestroy() {
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.reportChannel } });
    this.removeListeners();
  },
};
</script>
